import {
  IonButton,
  IonButtons,
  IonFooter,
  IonHeader,
  IonIcon,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/button";
import { Buttons } from "@/components/buttons";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { useBoomerForm } from "@/form/boomer/hook";
import { useValidator } from "@/form/common/validator";
import { crossIcon } from "@/lib/icons/cross.icon";
import { durationToMinutes, parseTimeString } from "@/utils/time";

interface BreakTimeModalProps {
  isOpen: boolean;
  onDidDismiss: () => void;
  onSubmit: (breakTimeMinutes: number) => Promise<void>;
  defaultBreakTime?: number;
}

export function BreakTimeModal({
  isOpen,
  onDidDismiss,
  onSubmit,
  defaultBreakTime = 0,
}: BreakTimeModalProps) {
  const { t } = useTranslation();
  const validator = useValidator();

  const [form, { Element: FormElement }] = useBoomerForm({
    defaultValues: {
      breakDuration:
        defaultBreakTime > 0
          ? `${Math.floor(defaultBreakTime / 60)
              .toString()
              .padStart(2, "0")}:${(defaultBreakTime % 60).toString().padStart(2, "0")}`
          : "00:30", // Default to 30 minutes
    },
    onSubmit: async ({ value }) => {
      const breakTimeMinutes = durationToMinutes(parseTimeString(value.breakDuration));
      await onSubmit(breakTimeMinutes);
      onDidDismiss();
    },
  });

  return (
    <ModalSheetAutoHeight
      isOpen={isOpen}
      onWillDismiss={onDidDismiss}
      onDidDismiss={() => form.reset()}
    >
      <FormElement>
        <IonHeader>
          <IonToolbar>
            <Buttons slot="start">
              <Button color="primary" onClick={onDidDismiss}>
                <IonIcon slot="icon-only" icon={crossIcon} className="size-6" />
              </Button>
            </Buttons>
            <IonTitle>{t("modals:break-time.title")}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <div className="ion-padding flex flex-col gap-6">
          <p className="text-sm text-txt-secondary">{t("modals:break-time.description")}</p>
          <form.AppField
            name="breakDuration"
            validators={{
              onBlur: validator.time({
                name: "breakDuration",
                schema: validator.required({ name: "breakDuration" }),
              }),
              onSubmit: validator.time({
                name: "breakDuration",
                schema: validator.required({ name: "breakDuration" }),
              }),
            }}
            children={(field) => (
              <field.InputField
                type="time"
                className="time-input"
                required
                labelPlacement="start"
                data-force-24h="true"
                withAsterisk={false}
                label={t("modals:break-time.duration-label")}
              />
            )}
          />
        </div>
        <IonFooter>
          <IonToolbar className="ion-pb-4 ion-pt-3">
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting] as const}
              children={([canSubmit, isSubmitting]) => (
                <Button type="submit" disabled={!canSubmit} expand="block">
                  {isSubmitting ? t("common:loading") : t("modals:break-time.submit")}
                </Button>
              )}
            />
          </IonToolbar>
        </IonFooter>
      </FormElement>
    </ModalSheetAutoHeight>
  );
}
